import {CarePlanConfigurationFeature} from '../../../../../backend/enums/care-plan-configuration-feature';
import {CarePlanConfigurationType} from '../../../../../backend/enums/care-plan-configuration-type';
import {CarePlanConfigurationItem} from '../../../../../backend/interfaces/care-plan-configuration-item';
import {
  ExportActivityTypeData,
  ExportCarePlanData,
  ExportCarePlanEntry,
  ExportCarePlanReportData,
  ExportGroupDetailsData,
} from './export-care-plan-types';

export function exportCarePlanProcessReportData(
  reportData: ExportCarePlanReportData
): ExportCarePlanData {
  const output: ExportCarePlanData = {
    entity: {id: reportData.entityData.id},
    sector: {
      id: reportData.sectorData.id!,
      name: reportData.sectorData.name,
    },
    group: {
      id: reportData.groupData.id,
      name: reportData.groupData.name,
    },
    details: [],
    tasks: [],
  };

  // Filter unwanted configs
  const filteredDetails = reportData.userGroupDetailsData.filter(
    (detail) => detail.config.type !== CarePlanConfigurationType.EmptySpace
  );

  // Process
  const details = filteredDetails.map(handleDetailData);
  const tasks = reportData.userActivityTypesData.map(handleTaskData);

  // Done
  output.tasks = tasks;
  output.details = details;

  return output;
}

function handleDetailData(detail: ExportGroupDetailsData): ExportCarePlanEntry {
  if (detail.config.multipleValues) {
    return {
      id: detail.config.id,
      name: detail.config.name,
      value:
        detail.config.type === 'array'
          ? ((detail.configValues as CarePlanConfigurationItem[] | undefined)?.map(
              (item) => item.name ?? ''
            ) ?? [])
          : ((detail.configValues as string | undefined) ?? ''),
      feature: detail.config.feature,
    };
  } else if (detail.config.type === 'array') {
    return {
      id: detail.config.id,
      name: detail.config.name,
      value: (detail.configValues as CarePlanConfigurationItem | undefined)?.name ?? '',
      feature: detail.config.feature,
    };
  } else {
    return {
      id: detail.config.id,
      name: detail.config.name,
      value: (detail.configValues as string | undefined) ?? '',
      feature: detail.config.feature,
    };
  }
}

function handleTaskData(task: ExportActivityTypeData): ExportCarePlanEntry[] {
  if (!task.configValues) return [];

  let result: ExportCarePlanEntry[] = [];

  for (const taskConfigValue of task.configValues) {
    const taskConfig = task.config.find((c) => c.id === taskConfigValue.carePlanConfigurationId);

    if (!taskConfig) {
      console.debug(
        `Task Config '${taskConfigValue.carePlanConfigurationId}' not found for activity type '${task.activityType.name}'`
      );

      continue;
    }

    let value: string | string[] | undefined;

    if (taskConfig.multipleValues) {
      value =
        taskConfig.type === 'array'
          ? taskConfigValue.carePlanConfigurationValue?.map((item: string) => {
              const configItem = taskConfig.arrayItems?.find((i) => i.id === item);
              return configItem ? (configItem.name ?? '') : (item ?? '');
            })
          : ((taskConfigValue.carePlanConfigurationValue as string | undefined) ?? '');
    } else if (taskConfig.type === 'array') {
      const configItem = taskConfig.arrayItems?.find(
        (item) => item.id === taskConfigValue.carePlanConfigurationValue
      );
      value = configItem ? configItem.name : taskConfigValue.carePlanConfigurationValue;
    } else if (taskConfig.feature === CarePlanConfigurationFeature.Name) {
      value = taskConfigValue.carePlanConfigurationValue || task.activityType.name;
    } else {
      value = taskConfigValue.carePlanConfigurationValue;
    }

    result.push({
      id: taskConfig.id,
      name: taskConfig.name,
      value: value ?? '',
      feature: taskConfig.feature,
    });
  }

  return result;
}
